import { Link, createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import {
	IconArrowLeft,
	IconCheck,
	IconMail,
	IconMapPin,
	IconPhone,
	IconX,
} from "@tabler/icons-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/convex-api";

export const Route = createFileRoute("/_authenticated/runners/$userId")({
	component: RunnerDetailPage,
});

function formatNaira(kobo: number): string {
	return `₦${(kobo / 100).toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
}

function RunnerDetailPage() {
	const { userId } = Route.useParams();
	const data = useQuery(api.admin.getRunnerFullProfile, {
		userId: userId as never,
	});
	const approveKyc = useMutation(api.runner.approveKyc);
	const rejectKyc = useMutation(api.runner.rejectKyc);

	const [rejectionReason, setRejectionReason] = useState("");
	const [isApproving, setIsApproving] = useState(false);
	const [isRejecting, setIsRejecting] = useState(false);
	const [showRejectForm, setShowRejectForm] = useState(false);
	const [message, setMessage] = useState<string | null>(null);

	if (data === undefined) {
		return (
			<div className="container mx-auto space-y-6 px-4 py-10 lg:px-6">
				<Skeleton className="h-8 w-48" />
				<div className="grid gap-6 lg:grid-cols-2">
					<Skeleton className="h-64" />
					<Skeleton className="h-64" />
				</div>
			</div>
		);
	}

	if (data === null) {
		return (
			<div className="container mx-auto px-4 py-10 lg:px-6">
				<p className="text-muted-foreground">Runner not found.</p>
				<Button asChild className="mt-4" variant="outline">
					<Link to="/runners">Back to Runners</Link>
				</Button>
			</div>
		);
	}

	const { user, runnerProfile, wallet, completedErrands, disputes } = data;
	const kycStatus = runnerProfile?.kycStatus ?? "pending";

	const handleApprove = async () => {
		setIsApproving(true);
		setMessage(null);
		try {
			await approveKyc({ runnerUserId: userId as never });
			setMessage("KYC approved successfully.");
		} catch {
			setMessage("Failed to approve KYC.");
		} finally {
			setIsApproving(false);
		}
	};

	const handleReject = async () => {
		if (!rejectionReason.trim()) {
			setMessage("Please provide a rejection reason.");
			return;
		}
		setIsRejecting(true);
		setMessage(null);
		try {
			await rejectKyc({
				runnerUserId: userId as never,
				reason: rejectionReason.trim(),
			});
			setMessage("KYC rejected.");
			setShowRejectForm(false);
			setRejectionReason("");
		} catch {
			setMessage("Failed to reject KYC.");
		} finally {
			setIsRejecting(false);
		}
	};

	const getKycBadge = () => {
		switch (kycStatus) {
			case "verified":
				return <Badge className="bg-green-500">Approved</Badge>;
			case "rejected":
				return <Badge variant="destructive">Rejected</Badge>;
			default:
				return <Badge variant="secondary">Pending</Badge>;
		}
	};

	return (
		<div className="container mx-auto px-4 py-10 lg:px-6">
			<div className="mb-6 flex items-center gap-4">
				<Button asChild size="sm" variant="ghost">
					<Link to="/runners">
						<IconArrowLeft className="mr-1 h-4 w-4" />
						Back
					</Link>
				</Button>
				<h1 className="font-bold text-2xl">{user.name}</h1>
				{getKycBadge()}
			</div>

			<div className="grid gap-6 lg:grid-cols-2">
				{/* User Info */}
				<Card>
					<CardHeader>
						<CardTitle>Personal Information</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3">
						<div className="flex items-center gap-2 text-sm">
							<IconMail className="h-4 w-4 text-muted-foreground" />
							<span>{user.email}</span>
							{data.emailVerified ? (
								<Badge className="bg-green-500 text-xs">Verified</Badge>
							) : (
								<Badge variant="destructive" className="text-xs">
									Unverified
								</Badge>
							)}
						</div>
						<div className="flex items-center gap-2 text-sm">
							<IconPhone className="h-4 w-4 text-muted-foreground" />
							<span>{data.phoneNumber ?? "No phone"}</span>
						</div>
						<div className="flex items-center gap-2 text-sm">
							<IconMapPin className="h-4 w-4 text-muted-foreground" />
							<span>{runnerProfile?.workLocation ?? "No location"}</span>
						</div>
						{runnerProfile?.gender && (
							<div className="text-sm">
								<span className="text-muted-foreground">Gender:</span>{" "}
								{runnerProfile.gender}
							</div>
						)}
						{user.birthday && (
							<div className="text-sm">
								<span className="text-muted-foreground">Birthday:</span>{" "}
								{user.birthday}
							</div>
						)}
						<div className="text-sm">
							<span className="text-muted-foreground">Joined:</span>{" "}
							{new Date(user.createdAt).toLocaleDateString()}
						</div>
					</CardContent>
				</Card>

				{/* KYC Details */}
				<Card>
					<CardHeader>
						<CardTitle>KYC Details</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3">
						<div className="text-sm">
							<span className="text-muted-foreground">NIN:</span>{" "}
							{runnerProfile?.nin ?? "Not provided"}
						</div>
						<div className="text-sm">
							<span className="text-muted-foreground">BVN:</span>{" "}
							{runnerProfile?.bvn ?? "Not provided"}
						</div>
						{runnerProfile?.kycSubmittedAt && (
							<div className="text-sm">
								<span className="text-muted-foreground">Submitted:</span>{" "}
								{new Date(runnerProfile.kycSubmittedAt).toLocaleString()}
							</div>
						)}
						{runnerProfile?.kycRejectionReason && (
							<div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm">
								<span className="font-medium text-red-700">
									Rejection Reason:
								</span>{" "}
								<span className="text-red-600">
									{runnerProfile.kycRejectionReason}
								</span>
							</div>
						)}

						{/* Document images */}
						<div className="space-y-2 pt-2">
							<p className="font-medium text-sm">Documents</p>
							<div className="grid grid-cols-2 gap-3">
								{data.selfieUrl ? (
									<div>
										<p className="mb-1 text-muted-foreground text-xs">Selfie</p>
										<a
											href={data.selfieUrl}
											target="_blank"
											rel="noopener noreferrer"
										>
											<img
												src={data.selfieUrl}
												alt="Selfie"
												className="h-32 w-full rounded-md border object-cover"
											/>
										</a>
									</div>
								) : (
									<div className="flex h-32 items-center justify-center rounded-md border bg-muted text-muted-foreground text-xs">
										No selfie
									</div>
								)}
								{data.ninDocumentUrl ? (
									<div>
										<p className="mb-1 text-muted-foreground text-xs">
											NIN Document
										</p>
										<a
											href={data.ninDocumentUrl}
											target="_blank"
											rel="noopener noreferrer"
										>
											<img
												src={data.ninDocumentUrl}
												alt="NIN"
												className="h-32 w-full rounded-md border object-cover"
											/>
										</a>
									</div>
								) : (
									<div className="flex h-32 items-center justify-center rounded-md border bg-muted text-muted-foreground text-xs">
										No NIN doc
									</div>
								)}
								{data.bvnDocumentUrl ? (
									<div>
										<p className="mb-1 text-muted-foreground text-xs">
											BVN Document
										</p>
										<a
											href={data.bvnDocumentUrl}
											target="_blank"
											rel="noopener noreferrer"
										>
											<img
												src={data.bvnDocumentUrl}
												alt="BVN"
												className="h-32 w-full rounded-md border object-cover"
											/>
										</a>
									</div>
								) : (
									<div className="flex h-32 items-center justify-center rounded-md border bg-muted text-muted-foreground text-xs">
										No BVN doc
									</div>
								)}
								{data.driversLicenseUrl ? (
									<div>
										<p className="mb-1 text-muted-foreground text-xs">
											Driver's License
										</p>
										<a
											href={data.driversLicenseUrl}
											target="_blank"
											rel="noopener noreferrer"
										>
											<img
												src={data.driversLicenseUrl}
												alt="License"
												className="h-32 w-full rounded-md border object-cover"
											/>
										</a>
									</div>
								) : (
									<div className="flex h-32 items-center justify-center rounded-md border bg-muted text-muted-foreground text-xs">
										No license
									</div>
								)}
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Next of Kin & Guarantors */}
				<Card>
					<CardHeader>
						<CardTitle>Next of Kin & Guarantors</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3">
						{runnerProfile?.nokName && (
							<div className="text-sm">
								<span className="text-muted-foreground">Next of Kin:</span>{" "}
								{runnerProfile.nokName} ({runnerProfile.nokPhone ?? "No phone"})
							</div>
						)}
						{runnerProfile?.guarantor1Name && (
							<div className="text-sm">
								<span className="text-muted-foreground">Guarantor 1:</span>{" "}
								{runnerProfile.guarantor1Name} (
								{runnerProfile.guarantor1Phone ?? "No phone"}) -{" "}
								{runnerProfile.guarantor1Relationship ?? ""}
							</div>
						)}
						{runnerProfile?.guarantor2Name && (
							<div className="text-sm">
								<span className="text-muted-foreground">Guarantor 2:</span>{" "}
								{runnerProfile.guarantor2Name} (
								{runnerProfile.guarantor2Phone ?? "No phone"}) -{" "}
								{runnerProfile.guarantor2Relationship ?? ""}
							</div>
						)}
						{runnerProfile?.previousWork && (
							<div className="text-sm">
								<span className="text-muted-foreground">Previous Work:</span>{" "}
								{runnerProfile.previousWork} (
								{runnerProfile.workDuration ?? "N/A"})
							</div>
						)}
						{runnerProfile?.vehicleType && (
							<div className="text-sm">
								<span className="text-muted-foreground">Vehicle:</span>{" "}
								{runnerProfile.vehicleType} {runnerProfile.vehicleBrand ?? ""} -{" "}
								{runnerProfile.plateNumber ?? "No plate"}
							</div>
						)}
					</CardContent>
				</Card>

				{/* Bank Details & Wallet */}
				<Card>
					<CardHeader>
						<CardTitle>Bank Details & Wallet</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3">
						{runnerProfile?.bankName ? (
							<>
								<div className="text-sm">
									<span className="text-muted-foreground">Bank:</span>{" "}
									{runnerProfile.bankName}
								</div>
								<div className="text-sm">
									<span className="text-muted-foreground">Account:</span>{" "}
									{runnerProfile.accountNumber ?? "N/A"} (
									{runnerProfile.accountName ?? "N/A"})
								</div>
								<div className="text-sm">
									<span className="text-muted-foreground">Verified:</span>{" "}
									{runnerProfile.bankVerified ? "Yes" : "No"}
								</div>
							</>
						) : (
							<p className="text-muted-foreground text-sm">
								No bank details provided.
							</p>
						)}
						{wallet && (
							<div className="mt-2 rounded-md border p-3">
								<p className="mb-1 font-medium text-sm">Wallet</p>
								<div className="text-sm">
									Available: {formatNaira(wallet.availableBalance)}
								</div>
								<div className="text-sm">
									Locked: {formatNaira(wallet.lockedBalance)}
								</div>
								<div className="text-muted-foreground text-xs">
									Status: {wallet.status}
								</div>
							</div>
						)}
					</CardContent>
				</Card>
			</div>

			{/* KYC Actions */}
			{kycStatus === "pending" && (
				<Card className="mt-6">
					<CardHeader>
						<CardTitle>KYC Actions</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="flex gap-3">
							<Button
								disabled={isApproving}
								onClick={handleApprove}
								className="bg-green-600 hover:bg-green-700"
							>
								<IconCheck className="mr-2 h-4 w-4" />
								{isApproving ? "Approving..." : "Approve KYC"}
							</Button>
							<Button
								disabled={isRejecting}
								onClick={() => setShowRejectForm(!showRejectForm)}
								variant="destructive"
							>
								<IconX className="mr-2 h-4 w-4" />
								Reject KYC
							</Button>
						</div>
						{showRejectForm && (
							<div className="space-y-3">
								<Textarea
									onChange={(e) => setRejectionReason(e.target.value)}
									placeholder="Reason for rejection..."
									value={rejectionReason}
								/>
								<Button
									disabled={isRejecting}
									onClick={handleReject}
									variant="destructive"
								>
									{isRejecting ? "Rejecting..." : "Confirm Rejection"}
								</Button>
							</div>
						)}
						{message && (
							<p className="text-muted-foreground text-sm">{message}</p>
						)}
					</CardContent>
				</Card>
			)}

			{/* Tabs: Errands & Disputes */}
			<div className="mt-6">
				<Tabs defaultValue="errands">
					<TabsList>
						<TabsTrigger value="errands">
							Completed Errands ({completedErrands.length})
						</TabsTrigger>
						<TabsTrigger value="disputes">
							Disputes ({disputes.length})
						</TabsTrigger>
					</TabsList>
					<TabsContent value="errands" className="mt-4">
						{completedErrands.length === 0 ? (
							<p className="text-muted-foreground text-sm">
								No completed errands.
							</p>
						) : (
							<div className="rounded-md border">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>Title</TableHead>
											<TableHead>Completed At</TableHead>
											<TableHead>Actions</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{completedErrands.map(
											(errand: (typeof completedErrands)[number]) => (
												<TableRow key={errand._id}>
													<TableCell className="font-medium">
														{errand.title}
													</TableCell>
													<TableCell>
														{errand.completedAt
															? new Date(
																	errand.completedAt,
																).toLocaleDateString()
															: "N/A"}
													</TableCell>
													<TableCell>
														<Button asChild size="sm" variant="ghost">
															<Link
																to="/errands/$errandId"
																params={{ errandId: errand._id }}
															>
																View
															</Link>
														</Button>
													</TableCell>
												</TableRow>
											),
										)}
									</TableBody>
								</Table>
							</div>
						)}
					</TabsContent>
					<TabsContent value="disputes" className="mt-4">
						{disputes.length === 0 ? (
							<p className="text-muted-foreground text-sm">No disputes.</p>
						) : (
							<div className="rounded-md border">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>Type</TableHead>
											<TableHead>Reason</TableHead>
											<TableHead>Status</TableHead>
											<TableHead>Created</TableHead>
											<TableHead>Actions</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{disputes.map((dispute: (typeof disputes)[number]) => (
											<TableRow key={dispute._id}>
												<TableCell>{dispute.disputeType}</TableCell>
												<TableCell className="max-w-[200px] truncate">
													{dispute.reason ?? "N/A"}
												</TableCell>
												<TableCell>
													{dispute.resolvedAt ? (
														<Badge className="bg-green-500">Resolved</Badge>
													) : (
														<Badge variant="destructive">Open</Badge>
													)}
												</TableCell>
												<TableCell>
													{new Date(dispute.createdAt).toLocaleDateString()}
												</TableCell>
												<TableCell>
													<Button asChild size="sm" variant="ghost">
														<Link
															to="/disputes/$disputeId"
															params={{ disputeId: dispute._id }}
														>
															View
														</Link>
													</Button>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						)}
					</TabsContent>
				</Tabs>
			</div>
		</div>
	);
}
